import { useState, useRef, useCallback, useEffect, type RefObject } from 'react';
import {
  submitChat,
  cancelChat,
  fetchHistory,
  fetchSession,
  deleteSession,
  fetchClientDetails,
  fetchPreferences,
  updatePreferences,
  ChatError,
  type ChatErrorKind,
  type ChatResponse as ChatResponseData,
  type HistoryItem,
  type Control,
  type DisambiguationCandidate,
  type OnboardingContent,
} from '@/lib/api';
import {
  useClientContext,
  contextFromHistoryItem,
  contextFromChatResponse,
  contextFromClientDetails,
  type ClientContextStore,
} from '@/lib/clientContext';
import { getOrCreateSessionId, clearSessionId, hasExistingSession, storageSet } from '@/lib/session';
import { useCreditBalance } from './useCreditBalance';
import type { CreditState, WalletData } from '@/lib/wallet';
import { ASSISTIVE_DISCLAIMER } from '@/lib/constants';
import { IressButton, IressIcon } from '@iress-oss/ids-components';
import { groupHistoryByDate } from '@/lib/history';
import { filterMarkdownTableEmptyColumns } from '@/lib/markdownTableFilter';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import { ChatResponse } from './ChatResponse';
import { CopyButton } from './CopyButton';
import { CreditLimitToast } from './CreditLimitToast';
import { DisambiguationTable, disambiguationCopyText } from './DisambiguationTable';
import { SkeletonLoader } from './SkeletonLoader';
import { WidgetSkeleton } from './WidgetSkeleton';
import { ErrorMessage } from './ErrorMessage';
import { PageError } from './PageError';
import { ClientContextTag } from './ClientContextTag';
import { HistoryList } from './HistoryList';
import { HistoryDetail } from './HistoryDetail';
import { OnboardingCard, StartSearchButton } from './OnboardingCard';
import { ContextualTip } from './ContextualTip';
import { FooterDisclaimer } from './FooterDisclaimer';
import './ChatApp.css';
import '@iress-oss/ids-components/dist/style.css';

interface Turn {
  id: number;
  query: string;
  response?: string;
  error?: string;
  status: 'loading' | 'done' | 'error' | 'credit_limit';
  /** UI control to render beneath a non-answer response (from the backend). */
  control?: Control;
  /**
   * Non-answer reason code from the backend (e.g. DATA_ABSENT, PERMISSION_DENIED);
   * null on a clean successful response. Used to distinguish a genuine success
   * (control=NONE, reasonCode=null) from a non-answer that carries control=NONE.
   */
  reasonCode?: string | null;
  /** 2–5 disambiguation candidates surfaced with this turn's response. */
  candidates?: DisambiguationCandidate[];
  /**
   * Leak-safe disambiguation discriminator for this turn: `'too_many'` renders
   * an "Add more context" button (Option C: re-populates the original query);
   * `'disambiguate'` renders the candidate pick-list.
   */
  disambiguationKind?: 'disambiguate' | 'too_many' | null;
  /**
   * When true, the CreditLimitToast for this specific turn has been dismissed.
   * The query bubble stays visible; only the toast is hidden.
   * New attempts while exhausted stack as separate turn instances.
   */
  dismissedCreditLimit?: boolean;
  /**
   * True when the adviser stopped this turn before it completed. The turn is
   * terminal ('done') with a fixed "Response stopped." message and renders no
   * affordance (no Copy / Change question / Refresh).
   */
  stopped?: boolean;
}

type Tab = 'chat' | 'history';
type HistoryView = 'list' | 'detail';

/**
 * Chat query failures whose kind is retry-recoverable are shown as the
 * pane-level {@link PageError} (Refresh re-sends the query). Non-recoverable
 * kinds (`session_expired`, `rate_limited`) keep the inline {@link ErrorMessage}
 * because a Refresh cannot resolve them.
 */
const PAGE_LEVEL_ERROR_KINDS: ReadonlySet<ChatErrorKind> = new Set([
  'service_unavailable',
  'network',
  'unknown',
]);

const INPUT_PLACEHOLDER = 'Ask about one client at a time';

interface ChatTurnProps {
  turn: Turn;
  onStop: () => void;
  onChangeQuestion: (query: string) => void;
  onRefresh: (query: string) => void;
  onSelectCandidate: (candidate: DisambiguationCandidate, query: string) => void;
  onAddMoreContext: (query: string) => void;
  onDismissCreditLimit: (id: number) => void;
  /**
   * The currently pinned client id (if any). Used to render the disambiguation
   * pick-list's resolved state: the candidate whose `clientId` equals this is
   * shown as chosen and the others become non-selectable. This works both live
   * (selection pins the client synchronously) and on session restore (the pin
   * is restored from the session's active client id), so no separate "selected"
   * field is persisted.
   */
  activeClientId?: string | null;
}

/** The "Searching…" status row with a Stop button. */
function TurnLoadingView({ onStop }: Readonly<{ onStop: () => void }>) {
  return (
    <>
      <div className="porter-chat__status">
        <p className="porter-chat__status-label">Searching…</p>
        <button
          className="porter-chat__stop"
          onClick={onStop}
          aria-label="Stop generating"
          type="button"
        >
          <span className="porter-chat__stop-label">Stop</span>
          <IressIcon name="stop_circle" className="porter-chat__stop-icon" />
        </button>
      </div>
      <SkeletonLoader />
    </>
  );
}

/** The optional Change-question / Refresh control beneath an answer. */
function TurnControl({
  control,
  reasonCode,
  disambiguationKind,
  query,
  onChangeQuestion,
  onRefresh,
}: Readonly<{
  control?: Control;
  reasonCode?: string | null;
  disambiguationKind?: 'disambiguate' | 'too_many' | null;
  query: string;
  onChangeQuestion: (query: string) => void;
  onRefresh: (query: string) => void;
}>) {
  if (control === 'REFRESH') {
    return (
      <div className="porter-chat__controls">
        <IressButton
          mode="secondary"
          onClick={() => onRefresh(query)}
        >
          <IressIcon name="refresh" textStyle="typography.body.md.strong" />
          Refresh
        </IressButton>
      </div>
    );
  }

  // Show "Change question" for explicit CHANGE_QUESTION control, or for any
  // NONE control with a non-null reasonCode that isn't handled by "Add more context".
  const showChangeQuestion =
    control === 'CHANGE_QUESTION' ||
    (control === 'NONE' && reasonCode != null && disambiguationKind !== 'too_many');

  if (showChangeQuestion) {
    return (
      <div className="porter-chat__controls">
        <IressButton mode="secondary" onClick={() => onChangeQuestion(query)}>
          Change question
        </IressButton>
      </div>
    );
  }

  return null;
}

/** The answered-turn body: response text plus its optional control and copy button. */
function TurnDoneView({
  turn,
  onChangeQuestion,
  onRefresh,
}: Readonly<{
  turn: Turn;
  onChangeQuestion: (query: string) => void;
  onRefresh: (query: string) => void;
}>) {
  if (!turn.response) return null;

  // A stopped turn ("Response stopped.") is terminal with no affordance — no
  // Copy, no Change question / Refresh control.
  if (turn.stopped) {
    return <ChatResponse text={turn.response} />;
  }

  // Copy is available for clean successes: control=NONE, reasonCode=null,
  // and not a too_many disambiguation (which has "Add more context" instead).
  const showCopy =
    turn.control === 'NONE' &&
    (turn.reasonCode === null || turn.reasonCode === undefined) &&
    turn.disambiguationKind !== 'too_many';

  // For a disambiguate turn the copy button is rendered by ChatTurn *below*
  // the DisambiguationTable (so it copies the message + the table). Suppress it
  // here to avoid a second copy button above the table that copies the message
  // only. `hasCandidates` mirrors the table's own render guard in ChatTurn.
  const hasCandidates = !!turn.candidates && turn.candidates.length > 0;
  const copyHandledBelow = turn.disambiguationKind === 'disambiguate' && hasCandidates;

  return (
    <>
      <ChatResponse text={turn.response} />
      {showCopy && !copyHandledBelow && (
        <CopyButton text={filterMarkdownTableEmptyColumns(turn.response)} />
      )}
      <TurnControl
        control={turn.control}
        reasonCode={turn.reasonCode}
        disambiguationKind={turn.disambiguationKind}
        query={turn.query}
        onChangeQuestion={onChangeQuestion}
        onRefresh={onRefresh}
      />
    </>
  );
}

/**
 * The pinned client id to use as the pick-list's resolved selection for THIS
 * turn, or null. Resolves only when the pinned client is actually one of this
 * turn's candidates, so an unrelated later pin never locks an earlier pick-list
 * and a turn where nothing was chosen stays fully selectable.
 */
function resolveSelectedClientId(
  turn: Turn,
  activeClientId: string | null | undefined,
): string | null {
  if (activeClientId == null || !turn.candidates?.length) return null;
  return turn.candidates.some((c) => c.clientId === activeClientId)
    ? activeClientId
    : null;
}

/** The disambiguation affordances for a done turn: pick-list (+copy) or too_many. */
function TurnDisambiguationView({
  turn,
  activeClientId,
  onSelectCandidate,
  onAddMoreContext,
}: Readonly<{
  turn: Turn;
  activeClientId?: string | null;
  onSelectCandidate: (candidate: DisambiguationCandidate, query: string) => void;
  onAddMoreContext: (query: string) => void;
}>) {
  const hasCandidates = !!turn.candidates && turn.candidates.length > 0;

  if (hasCandidates) {
    return (
      <>
        <DisambiguationTable
          candidates={turn.candidates!}
          onSelect={(candidate) => onSelectCandidate(candidate, turn.query)}
          selectedClientId={resolveSelectedClientId(turn, activeClientId)}
        />
        {/* Copy sits BELOW the table for a disambiguate turn and copies the
            message plus the candidate table (the table is a structured
            component, not part of turn.response). too_many carries no
            candidates, so it never reaches here — it gets "Add more context"
            instead. */}
        {turn.disambiguationKind === 'disambiguate' && turn.response && (
          <CopyButton
            text={disambiguationCopyText(turn.response, turn.candidates!)}
          />
        )}
      </>
    );
  }

  if (turn.disambiguationKind === 'too_many') {
    return (
      <div className="porter-chat__controls">
        <IressButton
          className="porter-chat__control"
          mode="secondary"
          onClick={() => onAddMoreContext(turn.query)}
        >
          Add more context
        </IressButton>
      </div>
    );
  }

  return null;
}

/**
 * Renders a single transcript turn. Delegates each status/affordance to a
 * small sub-view so no single function carries the whole branch count.
 */
function ChatTurn({
  turn,
  onStop,
  onChangeQuestion,
  onRefresh,
  onSelectCandidate,
  onAddMoreContext,
  onDismissCreditLimit,
  activeClientId,
}: Readonly<ChatTurnProps>) {
  const isDone = turn.status === 'done';
  return (
    <div className="porter-chat__turn">
      <div className="porter-chat__user-query">
        <p className="porter-chat__user-query-text">{turn.query}</p>
      </div>

      {turn.status === 'loading' && <TurnLoadingView onStop={onStop} />}

      {isDone && (
        <TurnDoneView
          turn={turn}
          onChangeQuestion={onChangeQuestion}
          onRefresh={onRefresh}
        />
      )}

      {isDone && (
        <TurnDisambiguationView
          turn={turn}
          activeClientId={activeClientId}
          onSelectCandidate={onSelectCandidate}
          onAddMoreContext={onAddMoreContext}
        />
      )}

      {turn.status === 'credit_limit' && !turn.dismissedCreditLimit && (
        <CreditLimitToast
          onDismiss={() => onDismissCreditLimit(turn.id)}
        />
      )}

      {turn.status === 'error' && turn.error && (
        <ErrorMessage message={turn.error} />
      )}
    </div>
  );
}

/**
 * Manages the session ID lifecycle:
 * - Initialises from sessionStorage (new tab → generates UUID, reload → reads existing)
 * - Ensures the UUID is persisted to sessionStorage after mount
 * - Returns whether a session restore fetch is needed (true = existing tab reload)
 */
function useSessionId(): {
  sessionId: string;
  setSessionId: React.Dispatch<React.SetStateAction<string>>;
  sessionRestoring: boolean;
  setSessionRestoring: React.Dispatch<React.SetStateAction<boolean>>;
} {
  const [sessionRestoring, setSessionRestoring] = useState<boolean>(
    () => hasExistingSession(),
  );
  const [sessionId, setSessionId] = useState<string>(() => getOrCreateSessionId());

  // Ensure the UUID is written to sessionStorage after mount — the useState
  // initialiser runs synchronously during render, before sessionStorage is
  // reliably available in the Web Component connectedCallback context.
  useEffect(() => {
    storageSet(sessionId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { sessionId, setSessionId, sessionRestoring, setSessionRestoring };
}

interface ChatPaneBodyProps {
  sessionRestoring: boolean;
  preferenceLoaded: boolean;
  onboardingDismissed: boolean | null;
  onboardingContent: OnboardingContent | null;
  doNotShowAgain: boolean;
  onDoNotShowAgainChange: (value: boolean) => void;
  chatError: { query: string } | null;
  turns: Turn[];
  onChatErrorRetry: () => void;
  onStop: () => void;
  onChangeQuestion: (query: string) => void;
  onRefresh: (query: string) => void;
  onSelectCandidate: (candidate: DisambiguationCandidate, query: string) => void;
  onAddMoreContext: (query: string) => void;
  onDismissCreditLimit: (id: number) => void;
  /** Pinned client id, threaded to each turn's disambiguation pick-list to
   * render its resolved state (chosen row + locked others). */
  activeClientId?: string | null;
}

/** Discriminates which top-level state the Chat pane body should render. */
type ChatBodyMode = 'loading' | 'onboarding' | 'error' | 'ready';

function resolveChatBodyMode(
  props: Pick<
    ChatPaneBodyProps,
    | 'sessionRestoring'
    | 'preferenceLoaded'
    | 'onboardingDismissed'
    | 'onboardingContent'
    | 'chatError'
  >,
): ChatBodyMode {
  if (props.sessionRestoring || !props.preferenceLoaded) return 'loading';
  if (!props.onboardingDismissed) {
    return props.onboardingContent ? 'onboarding' : 'ready';
  }
  if (props.chatError) return 'error';
  return 'ready';
}

/**
 * Renders the Chat pane's scrollable body. Extracted from {@link ChatApp} so the
 * component's mutually-exclusive display states are resolved once
 * ({@link resolveChatBodyMode}) instead of via repeated multi-condition guards,
 * keeping {@link ChatApp}'s cyclomatic complexity low.
 */
function ChatPaneBody(props: Readonly<ChatPaneBodyProps>) {
  const mode = resolveChatBodyMode(props);
  const { onboardingContent } = props;

  if (mode === 'loading') return <WidgetSkeleton />;

  if (mode === 'onboarding' && onboardingContent) {
    return (
      <OnboardingCard
        content={onboardingContent}
        doNotShowAgain={props.doNotShowAgain}
        onDoNotShowAgainChange={props.onDoNotShowAgainChange}
      />
    );
  }

  if (mode === 'error') {
    return <PageError onRetry={props.onChatErrorRetry} />;
  }

  // mode === 'ready': show the contextual tip for a returning user with an
  // empty transcript, then the transcript turns. Both require onboarding to be
  // dismissed (mirrors the original render guards).
  const ready = props.onboardingDismissed === true;
  const showTip =
    ready && props.turns.length === 0 && onboardingContent !== null;

  return (
    <>
      {showTip && onboardingContent && (
        <ContextualTip
          title={onboardingContent.contextualTip.title}
          description={onboardingContent.contextualTip.description}
        />
      )}
      {ready &&
        props.turns.map((turn) => (
          <ChatTurn
            key={turn.id}
            turn={turn}
            onStop={props.onStop}
            onChangeQuestion={props.onChangeQuestion}
            onRefresh={props.onRefresh}
            onSelectCandidate={props.onSelectCandidate}
            onAddMoreContext={props.onAddMoreContext}
            onDismissCreditLimit={props.onDismissCreditLimit}
            activeClientId={props.activeClientId}
          />
        ))}
    </>
  );
}

// ── ChatFooter sub-component ──────────────────────────────────────────────────
// Extracted from ChatApp's return to keep the parent function's complexity low.
// Owns the onboarding/post-onboarding footer branch (start-search button,
// client context tag, chat input, and footer disclaimer).

interface ChatFooterProps {
  preferenceLoaded: boolean;
  onboardingDismissed: boolean | null;
  clientContext: ClientContextStore;
  inputRef: RefObject<ChatInputHandle | null>;
  handleSubmit: (message: string) => void;
  isLoading: boolean;
  sessionRestoring: boolean;
  injectedValue: string | null;
  injectedValueNonce: number;
  creditState: CreditState;
  walletData: WalletData | null;
  handleSeeUsage: () => void;
  handleStartSearch: () => void;
  newChat: () => void;
}

function ChatFooter({
  preferenceLoaded,
  onboardingDismissed,
  clientContext,
  inputRef,
  handleSubmit,
  isLoading,
  sessionRestoring,
  injectedValue,
  injectedValueNonce,
  creditState,
  walletData,
  handleSeeUsage,
  handleStartSearch,
  newChat,
}: Readonly<ChatFooterProps>) {
  return (
    <div className="porter-chat__footer">
      {preferenceLoaded && !onboardingDismissed && (
        <StartSearchButton onClick={handleStartSearch} />
      )}
      {preferenceLoaded && onboardingDismissed && (
        <>
          {clientContext.context && (
            <ClientContextTag context={clientContext.context} onClear={newChat} />
          )}
          <ChatInput
            ref={inputRef}
            onSubmit={handleSubmit}
            loading={isLoading}
            disabled={isLoading || sessionRestoring}
            placeholder={INPUT_PLACEHOLDER}
            injectedValue={injectedValue}
            injectedValueNonce={injectedValueNonce}
            creditState={creditState}
            remainingPercentage={walletData?.remainingPercentage ?? null}
            onViewUsage={handleSeeUsage}
          />
        </>
      )}
    </div>
  );
}

/** Returns true when the last turn in the transcript is still loading. */
function hasLoadingTurn(turns: Turn[]): boolean {
  return turns.length > 0 && turns.at(-1)?.status === 'loading';
}

export function ChatApp({
  initialClientId = null,
}: Readonly<{ initialClientId?: string | null }> = {}) {
  // ── Tab + view state (owned here so switching tabs preserves everything) ──
  const [activeTab, setActiveTab] = useState<Tab>('chat');
  const [historyView, setHistoryView] = useState<HistoryView>('list');
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);

  // ── Session ID + restore flag ──
  const { sessionId, setSessionId, sessionRestoring, setSessionRestoring } = useSessionId();

  // ── AI credit balance ──────────────────────────────────────────────────────
  // All credit state, broadcast effects, refresh triggers, and callbacks are
  // encapsulated in useCreditBalance to keep ChatApp's cognitive complexity low.
  const {
    creditState,
    walletData,
    refreshWallet,
    interceptCreditLimit,
    dismissCreditLimitUpdate,
    handleSeeUsage,
  } = useCreditBalance();

  // ── Onboarding / Preference state ──
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState<boolean | null>(null);
  const [onboardingContent, setOnboardingContent] = useState<OnboardingContent | null>(null);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  // ── Chat thread state ──
  const [turns, setTurns] = useState<Turn[]>([]);
  const clientContext = useClientContext();

  // Pane-level Chat error: set when a query fails with a retry-recoverable
  // kind (503 / network / unknown). Holds the failed query so Refresh can
  // re-send it. `null` when there is no page-level Chat error.
  const [chatError, setChatError] = useState<{ query: string } | null>(null);

  // Shell-level open-Porter error (header only, no tab bar). Set when Porter's
  // open-time call — the preferences fetch (`GET /preferences`) — fails with a
  // backend failure or network issue. Refresh (handleOpenRetry) re-runs it.
  const [openError, setOpenError] = useState(false);

  // Re-population of the original message after a disambiguation selection
  // (Model X). The nonce forces the input to refill even if the text is
  // identical to a previous injection.
  const [injectedValue, setInjectedValue] = useState<string | null>(null);
  const [injectedValueNonce, setInjectedValueNonce] = useState(0);

  // ── History data state ──
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // ── Scroll position persistence for history list ──
  const [historyScrollPosition, setHistoryScrollPosition] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const historyAbortRef = useRef<AbortController | null>(null);
  // Holds the UI-minted query id for the in-flight request so
  // handleStop can address POST /chat/{id}/cancel while /chat is parked.
  const queryIdRef = useRef<string | null>(null);
  const nextTurnId = useRef(0);
  const inputRef = useRef<ChatInputHandle>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Focus the input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // ── Auto-detect client context on launch ────────────────────────────────
  // Porter is launched fresh each time the global-nav "Ask Porter" button is
  // clicked; main.tsx reads the in-view client id from
  // `window.XPLAN.apex.entityInfo` and passes it as `initialClientId`. When present AND this is a fresh session
  // (not a restore) AND no context is already set, fetch the display snapshot
  // and pin the context tag. Best-effort and NON-BLOCKING: the input is already
  // focused (above); a slow/failed fetch simply leaves the tag unset — it never
  // delays or errors the panel (Scenario 4). Retention (D4): we set only into an
  // empty context, so this never overwrites a restored/user-set client.
  useEffect(() => {
    if (initialClientId === null) return;
    // A restored session is the authority for the active client; don't race it.
    if (sessionRestoring) return;
    // Single-set: never overwrite an existing context.
    if (clientContext.context !== null) return;

    const controller = new AbortController();

    fetchClientDetails(initialClientId, controller.signal)
      .then((details) => {
        if (controller.signal.aborted) return;
        // Only pin the context tag when the detected id resolves to a valid
        // client. `fetchClientDetails` returns null on a 404 ("not a client"),
        // network error, or malformed body — in which case no client was
        // identified, so we set NO context (no tag, no current_client_id).
        // A bare "Client" tag on an unresolved id is misleading and violates
        // the "no client identified → no context" rule.
        if (details === null) return;
        clientContext.set(contextFromClientDetails(initialClientId, details));
      })
      .catch(() => {
        // fetchClientDetails never rejects, but guard anyway — no context.
      });

    return () => controller.abort();
    // Run once on mount; sessionRestoring transitions are handled by the guard
    // above (a restore that later sets context wins via the empty-context check
    // on the next render is not needed — we only auto-set when already empty).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionRestoring]);

  // ── Session restore on mount ──
  // On first render, fetch the existing session from the backend and restore
  // the thread + client context. On 404 (new session) this is a no-op.
  // We deliberately run this only once (empty dep array) — subsequent turns
  // are accumulated in React state and persisted via POST /chat.
  //
  // The fetch is deferred by one rAF so the skeleton is guaranteed to paint
  // for at least one frame before any data arrives (the network can respond
  // faster than a paint cycle, especially on localhost).
  useEffect(() => {
    // New tab: sessionRestoring starts false, nothing to fetch.
    if (!sessionRestoring) return;

    const controller = new AbortController();

    async function restoreSession(): Promise<void> {
      try {
        const session = await fetchSession(sessionId, controller.signal);
        if (controller.signal.aborted) return;

        if (session !== null) {
          const restoredTurns: Turn[] = session.turns.map((t, index) => ({
            id: index,
            query: t.query,
            response: t.response,
            status: 'done' as const,
            control: t.control as Control,
            reasonCode: t.reasonCode,
            candidates: t.candidates,
            disambiguationKind: t.disambiguationKind as Turn['disambiguationKind'],
          }));
          nextTurnId.current = restoredTurns.length;
          setTurns(restoredTurns);
          if (session.activeClientId !== null) {
            // Restore the FULL client context (id + point-in-time display
            // snapshot) so the context tag shows the client's name after a
            // reload, not a bare id. The ClientContext is EntityDetails + id, so
            // we build it inline (same shape selectCandidate uses). Details are
            // null for a session that never captured them, in which case the tag
            // falls back to the generic "Client" label (entityDisplayName).
            const details = session.activeClientDetails;
            clientContext.set({
              id: session.activeClientId,
              firstName: details?.firstName,
              lastName: details?.lastName,
              preferredName: details?.preferredName,
              dateOfBirth: details?.dateOfBirth,
              clientAdviserName: details?.clientAdviserName,
              preferredSuburb: details?.preferredSuburb,
              preferredState: details?.preferredState,
            });
          }
        }
      } catch (error_) {
        // Session restore is best-effort. A network or auth error on mount
        // is not surfaced as an error state — the adviser just starts fresh.
        console.debug('Session restore failed:', error_);
      } finally {
        if (!controller.signal.aborted) {
          setSessionRestoring(false);
        }
      }
    }

    const rafId = requestAnimationFrame(() => {
      restoreSession().catch(() => {
        // Outer catch for the rare case restoreSession itself throws outside
        // its own try/finally (e.g. a synchronous error before the await).
        setSessionRestoring(false);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      controller.abort();
      // Note: do NOT call setSessionRestoring(false) here. In React StrictMode
      // the cleanup fires between the first and second mount — calling it here
      // would reset the flag to false before the second mount's fetch starts,
      // so the skeleton would never show. When the component truly unmounts,
      // the aborted signal prevents any state update in the finally block.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only.

  // Porter's open-time call. `GET /preferences` gates the whole UI
  // (`preferenceLoaded`). It returns 200 even for a first-time user with no
  // record, so any rejection here is a genuine backend/network failure on
  // open → show the shell-level page-level error. Reused by handleOpenRetry
  // so Refresh re-runs the exact same call.
  const loadPreferences = useCallback(async () => {
    setOpenError(false);
    try {
      const pref = await fetchPreferences();
      setOnboardingDismissed(pref.onboardingDismissed);
      setOnboardingContent(pref.content);
      setPreferenceLoaded(true);
    } catch {
      // Backend failure or network issue while opening Porter. The message is
      // the shared "Something went wrong" — the kind is not surfaced at the
      // shell level (any open failure gets the same recoverable screen).
      setOpenError(true);
    }
  }, []);

  // Fetch preferences on mount (Porter open).
  useEffect(() => {
    void loadPreferences();
  }, [loadPreferences]);

  // Scroll the chat body to the bottom whenever turns change.
  useEffect(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  }, [turns]);

  // Restore scroll position when returning to history list view
  useEffect(() => {
    const body = bodyRef.current;
    const isHistoryList = activeTab === 'history' && historyView === 'list';
    if (!body || !isHistoryList) return;
    
    // Use requestAnimationFrame to ensure DOM is updated before restoring scroll
    const restoreScroll = () => {
      body.scrollTop = historyScrollPosition;
    };
    requestAnimationFrame(restoreScroll);
  }, [activeTab, historyView, historyScrollPosition]);

  const isLoading = hasLoadingTurn(turns);

  const loadHistory = useCallback(async () => {
    historyAbortRef.current?.abort();
    const controller = new AbortController();
    historyAbortRef.current = controller;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const items = await fetchHistory(controller.signal);
      if (!controller.signal.aborted) {
        setHistoryItems(items);
        setHistoryLoaded(true);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setHistoryError(
        err instanceof ChatError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      if (!controller.signal.aborted) setHistoryLoading(false);
    }
  }, []);

  // Fetch history the first time the History tab is opened (or after refresh).
  useEffect(() => {
    const shouldLoad = activeTab === 'history' && !historyLoaded && !historyLoading && !historyError;
    if (shouldLoad) {
      void loadHistory();
    }
  }, [activeTab, historyLoaded, historyLoading, historyError, loadHistory]);

  // ── Reset actions ──────────────────────────────────────────────────────
  // "New chat" and the tag × are identical: clear context + thread, fresh Chat.
  // Also deletes the active session from the backend (best-effort) and generates
  // a new sessionId so the next conversation starts a fresh session record.
  // Also dismisses onboarding if it's showing.
  const newChat = useCallback(() => {
    abortRef.current?.abort();
    clientContext.reset();
    setTurns([]);
    setOnboardingDismissed(true);
    setChatError(null);
    setActiveTab('chat');
    // Delete the current session on the backend (fire-and-forget).
    deleteSession(sessionId).catch(() => undefined);
    // Rotate the session ID: clear sessionStorage and generate a new UUID.
    clearSessionId();
    setSessionId(getOrCreateSessionId());
    // Use requestAnimationFrame to ensure input is rendered before focusing
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [clientContext, sessionId]);

  // ── Context set from a history entry (Search for client information) ─────
  const searchForClient = useCallback(
    (item: HistoryItem) => {
      abortRef.current?.abort();
      const ctx = contextFromHistoryItem(item);
      if (ctx) clientContext.set(ctx);
      setTurns([]);
      setChatError(null);
      setActiveTab('chat');
      inputRef.current?.focus();
      // Starting a fresh conversation for a different client — rotate the
      // session so the new turns aren't appended to the previous session.
      deleteSession(sessionId).catch(() => undefined);
      clearSessionId();
      setSessionId(getOrCreateSessionId());
    },
    [clientContext, sessionId],
  );

  const selectCandidate = useCallback(
    (candidate: DisambiguationCandidate, originalQuery: string) => {
      // Model X: set the active client context from the selected candidate
      // (clientId → id) and re-populate the ORIGINAL message unchanged for the
      // adviser to re-send. No criteria are injected into the text. The prior
      // table stays rendered as a transcript entry.
      clientContext.set({
        id: candidate.clientId,
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        preferredName: candidate.preferredName,
        dateOfBirth: candidate.dateOfBirth,
        clientAdviserName: candidate.clientAdviserName,
        preferredSuburb: candidate.preferredSuburb,
        preferredState: candidate.preferredState,
      });
      setInjectedValue(originalQuery);
      setInjectedValueNonce((n) => n + 1);
      inputRef.current?.focus();
    },
    [clientContext],
  );

  const addMoreContext = useCallback((originalQuery: string) => {
    // too_many (Option C): re-populate the ORIGINAL message unchanged so the
    // adviser can append narrowing detail (a last name, DOB, or adviser) and
    // re-send. No client context is set (identities were withheld), and the
    // prior message stays rendered as a transcript entry. The nonce forces the
    // input to refill even if the text matches a previous injection.
    setInjectedValue(originalQuery);
    setInjectedValueNonce((n) => n + 1);
    inputRef.current?.focus();
  }, []);

  const openDetail = useCallback((queryId: string) => {
    // Capture current scroll position before switching to detail view
    const body = bodyRef.current;
    if (body && historyView === 'list') {
      setHistoryScrollPosition(body.scrollTop);
    }
    setOpenEntryId(queryId);
    setHistoryView('detail');
  }, [historyView]);

  const backToList = useCallback(() => {
    setHistoryView('list');
    // Scroll restoration is handled by the useEffect above
  }, []);

  const handleStop = useCallback(() => {
    // Abort the local fetch (stops the UI from waiting on the response).
    abortRef.current?.abort();
    // Also signal the backend to halt the running agent task.
    // Fire-and-forget — best-effort, failure is not surfaced to the user.
    if (queryIdRef.current) {
      // Fire-and-forget: cancelChat already swallows its own errors; the
      // no-op catch keeps this promise-safe without the `void` operator.
      cancelChat(queryIdRef.current).catch(() => {});
      queryIdRef.current = null;
    }
    // Replace the in-flight loading turn with a terminal "Response stopped."
    // turn (status 'done') so the stop is acknowledged in the transcript and
    // the turn is NOT removed. The aborted fetch's catch in handleSubmit returns
    // early on `signal.aborted`, so it never overwrites this turn.
    //
    // DELIBERATE DESIGN / KNOWN RACE (parked backend reconciliation): the main
    // `/chat` fetch is aborted locally above, which DISCARDS any in-flight
    // response body — so there is no completed answer to "fall back to" here.
    // cancelChat is best-effort and fire-and-forget: if the backend had already
    // finished before the cancel landed, it persists the real answer (visible
    // later in History) while the live UI still shows "Response stopped.". This
    // is intentional — Stop honours the adviser's intent immediately, and the
    // backend already classifies a genuine cancel as USER_CANCELLED. Making the
    // UI instead await the cancel result and resurrect a completed response
    // would require NOT aborting the main fetch and racing the two connections;
    // that is the separately-parked reconciliation decision, not done here.
    //
    // control=NONE + stopped=true means no affordance (no Change question /
    // Refresh / Copy) is rendered beneath it.
    setTurns((prev) =>
      prev.map((t) =>
        t.status === 'loading'
          ? {
              ...t,
              status: 'done',
              response: 'Response stopped.',
              control: 'NONE',
              stopped: true,
            }
          : t,
      ),
    );
    inputRef.current?.focus();
  }, []);

  /** Hides the CreditLimitToast for a specific turn (toast dismissed, bubble kept). */
  const handleDismissCreditLimit = useCallback(
    (id: number) => setTurns(dismissCreditLimitUpdate(id)),
    [dismissCreditLimitUpdate],
  );

  // Apply a successful chat result to the transcript turn and pin the
  // client-context tag when appropriate. Extracted from handleSubmit to keep
  // that callback's cognitive complexity low.
  const applyChatSuccess = useCallback(
    (id: number, result: ChatResponseData) => {
      setTurns((prev) =>
        prev.map((t) =>
          t.id === id
            ? {
                ...t,
                response: result.response,
                control: result.control,
                reasonCode: result.reasonCode,
                candidates: result.disambiguationCandidates,
                disambiguationKind: result.disambiguationKind,
                status: 'done',
              }
            : t,
        ),
      );
      // Pin the client-context tag from a chat turn that resolved to a
      // single client — but ONLY when no tag is currently pinned (design
      // D5). A tag already pinned is never auto-swapped: a different-client
      // turn is blocked server-side (ClientConflictError → "one client at a
      // time") and returns no entity; only the user unpins (× / New chat).
      if (!clientContext.currentClientId) {
        const ctx = contextFromChatResponse(result);
        if (ctx) clientContext.set(ctx);
      }
      // A new query was answered, so the persisted history timeline is now
      // stale. Invalidate it so the History tab refetches on next entry
      // (the auto-load effect keys off historyLoaded). The History pane
      // stays mounted while chatting, so without this it would show a
      // stale list until a manual Refresh.
      setHistoryLoaded(false);
      // Re-fetch credit balance after query response is returned (AC4)
      refreshWallet();
    },
    [clientContext.currentClientId, clientContext.set, refreshWallet],
  );

  // Route a failed chat query. Retry-recoverable failures (503 / network /
  // unknown) take over the Chat pane as a page-level error (Refresh re-sends
  // the same query); non-recoverable failures (session expired, rate limited)
  // stay inline in the transcript turn. Extracted from handleSubmit to keep
  // that callback's cognitive complexity low.
  const applyChatFailure = useCallback(
    (id: number, message: string, err: unknown) => {
      const kind: ChatErrorKind =
        err instanceof ChatError ? err.kind : 'unknown';

      if (PAGE_LEVEL_ERROR_KINDS.has(kind)) {
        setTurns([]);
        setChatError({ query: message });
        // Re-fetch credit balance after query failure
        refreshWallet();
        return;
      }

      const msg =
        err instanceof ChatError
          ? err.message
          : 'Something went wrong. Please try again.';
      setTurns((prev) =>
        prev.map((t) =>
          t.id === id ? { ...t, error: msg, status: 'error' } : t,
        ),
      );
      // Re-fetch credit balance after query failure
      refreshWallet();
    },
    [refreshWallet],
  );

  const handleSubmit = useCallback(
    async (message: string) => {
      // ── Credit-limit intercept ─────────────────────────────────────────
      // When exhausted: interceptCreditLimit returns a credit_limit turn;
      // add it to the transcript and bail out before calling the agent.
      const creditLimitTurn = interceptCreditLimit(message, () => nextTurnId.current++);
      if (creditLimitTurn !== null) {
        setTurns((prev) => [...prev, creditLimitTurn]);
        return;
      }

      // Ensure sessionId is in sessionStorage before sending — guards against
      // the Web Component first-open timing window where the useState initialiser
      // ran before sessionStorage was ready.
      storageSet(sessionId);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      // Mint a fresh UUID for every submit so the UI can address
      // /chat/{id}/cancel while the request is in flight (see design.md §4).
      const queryId = crypto.randomUUID();
      queryIdRef.current = queryId;
      const id = nextTurnId.current++;

      setChatError(null);
      setTurns((prev) => [...prev, { id, query: message, status: 'loading' }]);
      try {
        const result = await submitChat(
          message,
          controller.signal,
          clientContext.currentClientId,
          queryId,
          sessionId,
        );
        if (!controller.signal.aborted) applyChatSuccess(id, result);
      } catch (err) {
        if (controller.signal.aborted) return;
        applyChatFailure(id, message, err);
      }
    },
    [
      interceptCreditLimit,
      clientContext.currentClientId,
      sessionId,
      applyChatSuccess,
      applyChatFailure,
    ],
  );

  // Resolve the open entry + its date label for the detail view.
  const openEntry = historyItems.find((i) => i.queryId === openEntryId) ?? null;
  const openEntryDateLabel = openEntry
    ? groupHistoryByDate([openEntry])[0]?.dateLabel ?? ''
    : '';

  // Defensive fallback: detail view requested but the entry is gone.
  useEffect(() => {
    const isStaleDetail = historyView === 'detail' && openEntryId !== null && !openEntry && historyLoaded;
    if (isStaleDetail) {
      setHistoryView('list');
      setOpenEntryId(null);
    }
  }, [historyView, openEntryId, openEntry, historyLoaded]);

  // Handle tab switching with scroll preservation
  const switchToTab = useCallback((tab: Tab) => {
    // If switching away from history list, capture scroll position
    const leavingHistoryList = activeTab === 'history' && historyView === 'list' && tab !== 'history';
    if (leavingHistoryList) {
      const body = bodyRef.current;
      if (body) {
        setHistoryScrollPosition(body.scrollTop);
      }
    }
    setActiveTab(tab);
  }, [activeTab, historyView]);

  // "Change question" control: copy the adviser's original query back into the
  // input (and focus it) so they can edit and resubmit.
  const handleChangeQuestion = useCallback((query: string) => {
    inputRef.current?.setText(query);
  }, []);

  // "Refresh" control: retry the same query (transient system error).
  const handleRefresh = useCallback(
    (query: string) => {
      void handleSubmit(query);
    },
    [handleSubmit],
  );

  // Handle onboarding dismissal (Start Searching button)
  const handleStartSearch = useCallback(() => {
    setOnboardingDismissed(true);
    if (doNotShowAgain) {
      // Fire-and-forget update to backend
      updatePreferences(true).catch(() => {
        // Silent failure - preference not persisted but UX continues
      });
    }
    // Use requestAnimationFrame to ensure input is rendered before focusing
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [doNotShowAgain]);

  // Pane-level Chat error Refresh: re-send the same failed query. On success
  // the transcript loads; on repeat failure the same page-level error returns.
  const handleChatErrorRetry = useCallback(() => {
    if (chatError) void handleSubmit(chatError.query);
  }, [chatError, handleSubmit]);

  // Shell-level open error Refresh: re-run Porter's open-time call
  // (`loadPreferences`). On success the normal UI loads; on repeat failure the
  // same shell-level error re-renders (loadPreferences re-sets openError).
  const handleOpenRetry = useCallback(() => {
    void loadPreferences();
  }, [loadPreferences]);

  // Shell-level open-Porter error: replaces the tab bar + panes, leaving only
  // the header (rendered by the surrounding shell). Triggered when Porter's
  // open-time preferences fetch fails; `handleOpenRetry` retries that call.
  if (openError) {
    return (
      <div className="porter-chat">
        <PageError onRetry={handleOpenRetry} />
      </div>
    );
  }

  return (
    <div className="porter-chat">
      {/* ── Tab bar + New chat (persistent) ── */}
      <div className="porter-chat__tabs">
        <div className="porter-chat__tablist" role="tablist">
          <button
            className="porter-chat__tab"
            role="tab"
            aria-selected={activeTab === 'chat'}
            data-active={activeTab === 'chat'}
            onClick={() => switchToTab('chat')}
            type="button"
          >
            Chat
          </button>
          <button
            className="porter-chat__tab"
            role="tab"
            aria-selected={activeTab === 'history'}
            data-active={activeTab === 'history'}
            onClick={() => switchToTab('history')}
            type="button"
          >
            History
          </button>
        </div>
        <button
          className="porter-chat__new-chat"
          onClick={newChat}
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M13.333 1.3335H2.66634C1.93301 1.3335 1.33301 1.9335 1.33301 2.66683V14.6668L3.99967 12.0002H13.333C14.0663 12.0002 14.6663 11.4002 14.6663 10.6668V2.66683C14.6663 1.9335 14.0663 1.3335 13.333 1.3335ZM13.333 10.6668H3.44634L2.66634 11.4468V2.66683H13.333V10.6668Z" fill="currentColor" />
            <path d="M7.99967 10.0002L9.04634 7.7135L11.333 6.66683L9.04634 5.62016L7.99967 3.3335L6.95301 5.62016L4.66634 6.66683L6.95301 7.7135L7.99967 10.0002Z" fill="currentColor" />
          </svg>
          New chat
        </button>
      </div>

      {/* ── Chat tab ── */}
      <div className="porter-chat__pane" hidden={activeTab !== 'chat'}>
        <div className="porter-chat__body" ref={bodyRef}>
          <ChatPaneBody
            sessionRestoring={sessionRestoring}
            preferenceLoaded={preferenceLoaded}
            onboardingDismissed={onboardingDismissed}
            onboardingContent={onboardingContent}
            doNotShowAgain={doNotShowAgain}
            onDoNotShowAgainChange={setDoNotShowAgain}
            chatError={chatError}
            turns={turns}
            onChatErrorRetry={handleChatErrorRetry}
            onStop={handleStop}
            onChangeQuestion={handleChangeQuestion}
            onRefresh={handleRefresh}
            onSelectCandidate={selectCandidate}
            onAddMoreContext={addMoreContext}
            onDismissCreditLimit={handleDismissCreditLimit}
            activeClientId={clientContext.currentClientId}
          />
        </div>

        {/* Assistive-use reminder: fixed below the scrolling message area (a
            direct child of the pane, not of .porter-chat__body) so it stays
            visible while the adviser scrolls back through a long transcript.
            Ungated by chatError — the verification reminder is a standing
            notice even when the footer input is hidden on a page-level error. */}
        <p className="porter-chat__disclaimer" role="note">
          {ASSISTIVE_DISCLAIMER}
        </p>

        {/* Footer hidden during page-level error (chatError) per design */}
        {!chatError && (
          <ChatFooter
            preferenceLoaded={preferenceLoaded}
            onboardingDismissed={onboardingDismissed}
            clientContext={clientContext}
            inputRef={inputRef}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            sessionRestoring={sessionRestoring}
            injectedValue={injectedValue}
            injectedValueNonce={injectedValueNonce}
            creditState={creditState}
            walletData={walletData}
            handleSeeUsage={handleSeeUsage}
            handleStartSearch={handleStartSearch}
            newChat={newChat}
          />
        )}
      </div>

      {/* ── History tab ── */}
      <div className="porter-chat__pane" hidden={activeTab !== 'history'}>
        <div className="porter-chat__body">
          {historyView === 'detail' && openEntry ? (
            <HistoryDetail
              item={openEntry}
              dateLabel={openEntryDateLabel}
              onBack={backToList}
              onSearchForClient={searchForClient}
            />
          ) : (
            <HistoryList
              items={historyItems}
              loading={historyLoading}
              error={historyError}
              onSelect={openDetail}
              onRefresh={loadHistory}
            />
          )}
        </div>
      </div>

      {/* ── Shared footer disclaimer (visible across Chat & History) ── */}
      {onboardingContent?.disclaimer && (
        <div className="porter-chat__disclaimer">
          <FooterDisclaimer text={onboardingContent.disclaimer} />
        </div>
      )}
    </div>
  );
}
